import {
  cashDrawerOpeningResponseSchema,
  cashDrawerOpeningResultResponseSchema,
  type CashDrawerOpening,
  type CashDrawerOpeningInput,
} from "@cosmo/contracts";
import { pulseEpsonCashDrawer, type EpsonDrawerConfig } from "./epson-drawer";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const configurationStorageKey = "cosmoEthernetCashDrawerConfiguration:v1";

export type CashDrawerConfiguration = EpsonDrawerConfig & {
  terminalId: string;
  terminalLabel: string;
};

export type AuditedCashDrawerResult = {
  status: "OPENED" | "ALREADY_RECORDED" | "FAILED";
  message: string;
  opening: CashDrawerOpening | null;
};

function defaultConfiguration(): CashDrawerConfiguration {
  return {
    terminalId: crypto.randomUUID(),
    terminalLabel: "Caja principal",
    printerAddress: "192.168.192.168",
    deviceId: "local_printer",
    drawerPin: 2,
  };
}

function validConfiguration(value: unknown): value is CashDrawerConfiguration {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<CashDrawerConfiguration>;
  return typeof candidate.terminalId === "string"
    && /^[0-9a-f-]{36}$/iu.test(candidate.terminalId)
    && typeof candidate.terminalLabel === "string" && candidate.terminalLabel.trim().length >= 2
    && typeof candidate.printerAddress === "string" && /^\d{1,3}(?:\.\d{1,3}){3}$/u.test(candidate.printerAddress)
    && typeof candidate.deviceId === "string" && /^[A-Za-z0-9_-]{1,64}$/u.test(candidate.deviceId)
    && (candidate.drawerPin === 2 || candidate.drawerPin === 5);
}

export function loadCashDrawerConfiguration(): CashDrawerConfiguration {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(configurationStorageKey) || "null") as unknown;
    if (validConfiguration(parsed)) return parsed;
  } catch {
    // Se reemplaza una configuración local dañada por los valores seguros conocidos.
  }
  const configuration = defaultConfiguration();
  saveCashDrawerConfiguration(configuration);
  return configuration;
}

export function saveCashDrawerConfiguration(configuration: CashDrawerConfiguration): void {
  window.localStorage.setItem(configurationStorageKey, JSON.stringify({
    ...configuration,
    terminalLabel: configuration.terminalLabel.trim(),
    printerAddress: configuration.printerAddress.trim(),
    deviceId: configuration.deviceId.trim(),
  }));
}

async function responseMessage(response: Response): Promise<string> {
  try {
    const body = await response.json() as { error?: { message?: string } };
    return body.error?.message || "No fue posible registrar la apertura de caja.";
  } catch {
    return "No fue posible registrar la apertura de caja.";
  }
}

async function reportResult(openingId: string, body: unknown): Promise<CashDrawerOpening | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/cash-drawer/openings/${openingId}/result`, {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        return cashDrawerOpeningResultResponseSchema.parse(await response.json()).data;
      }
    } catch {
      // Un segundo intento cierra la bitácora ante fallas transitorias de red.
    }
  }
  return null;
}

export async function openAuditedCashDrawer(
  operation: Omit<CashDrawerOpeningInput, keyof CashDrawerConfiguration>,
  configuration = loadCashDrawerConfiguration(),
): Promise<AuditedCashDrawerResult> {
  let opening: CashDrawerOpening | null = null;
  try {
    const response = await fetch(`${apiBaseUrl}/api/v1/cash-drawer/openings`, {
      method: "POST",
      credentials: "include",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ ...operation, ...configuration }),
    });
    if (!response.ok) {
      return { status: "FAILED", message: await responseMessage(response), opening: null };
    }
    const requested = cashDrawerOpeningResponseSchema.parse(await response.json()).data;
    opening = requested.opening;
    if (!requested.shouldOpen) {
      return {
        status: "ALREADY_RECORDED",
        message: "La caja ya fue solicitada para este cobro; no se repitió la apertura.",
        opening,
      };
    }

    const pulse = await pulseEpsonCashDrawer(configuration, opening.id);
    const resultBody = pulse.outcome === "OPENED"
      ? { outcome: "OPENED", errorCode: null, errorMessage: null }
      : pulse;
    const reportedOpening = await reportResult(opening.id, resultBody);
    if (pulse.outcome === "FAILED") {
      return {
        status: "FAILED",
        message: reportedOpening
          ? `${pulse.errorMessage} El pago no se modificó y la incidencia quedó registrada.`
          : `${pulse.errorMessage} El pago no se modificó; la solicitud quedó pendiente de cierre en la bitácora.`,
        opening: reportedOpening || opening,
      };
    }
    return {
      status: "OPENED",
      message: reportedOpening
        ? "Caja abierta y registrada correctamente."
        : "La caja abrió; la solicitud quedó pendiente de cierre en la bitácora.",
      opening: reportedOpening || opening,
    };
  } catch {
    return {
      status: "FAILED",
      message: "No fue posible completar la apertura auditada. El pago no se modificó.",
      opening,
    };
  }
}
